import axios from "axios";
import { logWarn } from "@/lib/log";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const http = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
const refresh = () => {
  if (!refreshing) refreshing = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).finally(() => { refreshing = null; });
  return refreshing;
};

const suspended = (e) => e?.response?.status === 403 && e.response.data?.detail?.code === "suspended";

http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    if (err.response?.status === 402) window.dispatchEvent(new CustomEvent("paywall", { detail: err.response.data?.detail }));
    if (suspended(err)) window.dispatchEvent(new CustomEvent("account-suspended", { detail: err.response.data.detail }));
    if (err.response?.status === 401 && !cfg._retry && !cfg.url.includes("/auth/")) {
      cfg._retry = true;
      try {
        await refresh();
        return http(cfg);
      } catch (refreshErr) {
        if (suspended(refreshErr)) window.dispatchEvent(new CustomEvent("account-suspended", { detail: refreshErr.response.data.detail }));
        logWarn("Session refresh failed", refreshErr);
      }
    }
    return Promise.reject(err);
  }
);

export function formatError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.message || d.msg || String(d);
}

export async function streamChat(chatId, body, onEvent, signal) {
  const send = () =>
    fetch(`${API}/chats/${chatId}/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  let res = await send();
  if (res.status === 401) {
    await refresh().catch((e) => logWarn("Session refresh failed", e));
    res = await send();
  }
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    if (res.status === 402) {
      window.dispatchEvent(new CustomEvent("paywall", { detail: j.detail }));
      const e = new Error(j.detail?.message || "Upgrade needed");
      e.paywall = true;
      throw e;
    }
    throw new Error(typeof j.detail === "string" ? j.detail : "Failed to send");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (line.startsWith("data:")) onEvent(JSON.parse(line.slice(5)));
    }
  }
}
